import { convertMeasuresToSeconds } from "../../util/convertMeasuresToSeconds";
import { audioCtx } from "../../util/audioContext";
import getPan from "../games/mixpuzzles/getPan";
import getVolume from "./processors/getVolume";
import Equalizer from "./processors/Equalizer/Equalizer";
import { Looop } from "shared/games/Looop";
import fetchAudioFileByUrl from "shared/games/fetchAudioFileByUrl";
import { PlaybackControls } from "./types";

export class SingleAudioPlayer {
  public id: number;

  private source: AudioBufferSourceNode;
  private buffer?: AudioBuffer;

  private equalizer: Equalizer;
  private gainNode: GainNode;
  private volumeFaderNode: GainNode;
  private stereoPannerNode: StereoPannerNode;
  private url: string;
  private looop: Looop;

  private disposed: boolean = false;
  private playing: boolean = false;
  private playbackControls: PlaybackControls = {
    pan: 0,
    volume: 0,
    muted: false,
    eq: {},
  };

  constructor(url: string, looop: Looop) {
    this.id = Math.random();
    this.url = url;
    this.looop = looop;

    this.source = audioCtx().createBufferSource();
    this.gainNode = audioCtx().createGain();
    this.equalizer = new Equalizer(this.id);
    this.stereoPannerNode = audioCtx().createStereoPanner();
    this.volumeFaderNode = audioCtx().createGain();

    this.source.connect(this.gainNode);
    this.gainNode.connect(this.equalizer.node);
    this.equalizer.connect(this.volumeFaderNode);
    this.volumeFaderNode.connect(this.stereoPannerNode);
    this.stereoPannerNode.connect(audioCtx().destination);

    this.syncLoopPointsWithMixPuzzle();
  }

  //
  // Public
  //

  public start = (time?: number) => {
    if (this.disposed) {
      return;
    }

    if (this.playing) {
      return;
    }

    this.source.start(time);
    this.playing = true;
  };

  public stop = (time?: number) => {
    if (this.disposed) {
      return;
    }

    if (!this.playing) {
      return;
    }

    this.source.stop(time);
    this.resetSource();
  };

  public setPlaybackStatus = (
    status: Partial<PlaybackControls>,
    time?: number
  ) => {
    if (this.disposed) {
      return;
    }

    time = time ?? audioCtx().currentTime;

    this.playbackControls = {
      ...this.playbackControls,
      ...status,
    };

    if (this.playbackControls.muted) {
      this.volumeFaderNode.gain.linearRampToValueAtTime(0.001, time);
    } else {
      this.volumeFaderNode.gain.linearRampToValueAtTime(
        getVolume(this.playbackControls),
        time
      );
    }

    this.equalizer.setValueAtTime(this.playbackControls.eq, time);

    this.stereoPannerNode.pan.linearRampToValueAtTime(
      this.playbackControls.pan,
      time
    );
  };

  public load = () => {
    return new Promise((res, rej) => {
      if (this.disposed) {
        return rej("already disposed");
      }

      const fn = async () => {
        const response = await fetchAudioFileByUrl(this.url);

        audioCtx().decodeAudioData(
          response,
          (buffer) => {
            this.buffer = buffer;
            this.source.buffer = buffer;
            res(this);
          },
          () => {
            console.error(`failed to load: ${this.url}`);
            rej(this);
          }
        );
      };

      fn();
    });
  };

  public syncLoopPointsWithMixPuzzle = (looop?: Looop) => {
    if (this.disposed) {
      return;
    }

    if (looop) {
      this.looop = looop;
    }

    this.source.loop = true;
    this.source.loopStart = 0;
    this.source.loopEnd = convertMeasuresToSeconds(
      this.looop.measures,
      this.looop.bpm
    );
  };

  public dispose = () => {
    if (this.disposed) {
      return;
    }

    this.disposed = true;

    const timeOffsetToPreventPops = 0.025;
    this.gainNode.gain.linearRampToValueAtTime(
      0,
      audioCtx().currentTime + timeOffsetToPreventPops
    );

    setTimeout(() => {
      this.source.disconnect();
      this.source = audioCtx().createBufferSource();
      this.gainNode.disconnect();
      this.stereoPannerNode.disconnect();
      this.volumeFaderNode.disconnect();
      this.equalizer.disconnect();
    }, timeOffsetToPreventPops * 1000);
  };

  //
  // Private
  //

  private resetSource = () => {
    if (this.disposed) {
      return;
    }

    if (this.playing) {
      this.source.stop();
    }

    this.source.disconnect();
    this.source = audioCtx().createBufferSource();
    this.source.connect(this.gainNode);

    this.syncLoopPointsWithMixPuzzle();
    this.playing = false;

    if (this.buffer) {
      this.source.buffer = this.buffer;
    } else {
      this.load().then(() => {
        console.log("a buffer should be present — this shouldn't happen");
      });
    }
  };
}
